import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { RendererParams } from '@/components/Form';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { RecurringSessionCompletionRollupTypeEnum, RecurringTypeEnum } from '@/types/schema';

import { EventFormInput } from '../../../../types';
import { getNewRecurrenceSchedule } from '../RecurrenceSchedule/utils';

interface RecurringCategoryProps {
  disabled?: boolean;
  value: RecurringTypeEnum;
  onChange: (val: RecurringTypeEnum) => void;
}

function RecurringCategory({ disabled, value, onChange }: RecurringCategoryProps) {
  const { t } = useTranslation();

  const onToggleRecurring = useCallback(
    (val: boolean) => {
      onChange(val ? RecurringTypeEnum.Recurring : RecurringTypeEnum.NonRecurring);
    },
    [onChange],
  );

  return (
    <SwitchWithInfo
      checked={value === RecurringTypeEnum.Recurring}
      classes={{ container: 'ms-8' }}
      description={t('Events:RecurringSession.RecurringSessionDescription')}
      disabled={disabled}
      label={t('Events:RecurringSession.RecurringSession')}
      onChange={onToggleRecurring}
    />
  );
}

const renderRecurringCategory =
  (isEditMode: boolean) =>
  ({
    value,
    onChange,
    values,
    setFieldValue,
  }: RendererParams<RecurringTypeEnum | null | undefined, EventFormInput>) => {
    const _onChange = (val: RecurringTypeEnum) => {
      if (val === RecurringTypeEnum.Recurring && !values.recurrenceSchedule) {
        setFieldValue('recurrenceSchedule', getNewRecurrenceSchedule(values), false);
      }
      if (val === RecurringTypeEnum.Recurring) {
        setFieldValue(
          'completionSettings.recurringSessionCompletionRollupType',
          RecurringSessionCompletionRollupTypeEnum.Highest,
          true,
        );
      } else if (val === RecurringTypeEnum.NonRecurring) {
        setFieldValue('completionSettings.recurringSessionCompletionRollupType', undefined, true);
      }
      onChange(val);
    };

    return (
      <RecurringCategory
        disabled={isEditMode}
        value={value || RecurringTypeEnum.NonRecurring}
        onChange={_onChange}
      />
    );
  };

export default renderRecurringCategory;
